import {useQuery} from "@apollo/client";
import {BarEventStatus} from "@uptime-com/uptimebar";
import classNames from "classnames";
import {format} from "date-fns";
import type {ReactElement, ReactNode} from "react";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {DateRange} from "react-day-picker";
import {useParams} from "react-router";

import {DatePicker} from "@/components/datePicker/DatePicker.tsx";
import {useDateRange} from "@/components/datePicker/DatePickerURLRangeParamsContext.ts";
import {KPICard} from "@/components/KPICard/KPICard.tsx";
import {NavTabItem} from "@/components/navTabs/NavTabs.types.ts";
import {
  GQL_PRIVATE_STATUSPAGE_HISTORY_QUERY,
  GQL_PUBLIC_STATUSPAGE_HISTORY_QUERY,
} from "@/lib/queries/statusPageConsumption.ts";
import {
  Incident as IncidentModel,
  IncidentState,
  IncidentType,
  StatusPageLayoutPreset,
  StatusPageTheme,
  StatusPageTimeFormat,
  UptimeCalculationType,
} from "@/lib/types/statuspages.ts";
import {ScrollableUptimeBar} from "@/pages/statuspages/consumption/components/ScrollableUptimeBar/ScrollableUptimeBar.tsx";

import {useStatusPageData} from "../../hooks/useStatusPageData.ts";
import {
  CurrentStatusTabType,
  HistoryTabType,
  type ComponentHistoryEntry,
  type PublicComponentStatusInfo,
  type PublicStatusPageHistoryResult,
  type PublicStatusPageIncident,
} from "../../types";
import {DATE_RANGE_END_PARAM, DATE_RANGE_START_PARAM, defaultDateRangeForDays} from "../../utils/dateRange.ts";
import {
  buildServiceComponentMap,
  formatDowntimeDuration,
  generateEventsFromDowntimes,
  generateEventsFromIncidents,
} from "../../utils/uptimeBarEvents.ts";
import {formatUptimePercentage} from "../../utils/uptimeFormat.ts";
import {ComponentHistoryTable} from "../currentstatus/components/ComponentHistoryTable.tsx";
import {ComponentHistoryTableEntry} from "../currentstatus/components/ComponentHistoryTable.types.ts";
import {IncidentsList} from "../currentstatus/components/IncidentsList.tsx";
import {StatusTabs} from "../currentstatus/components/StatusTabs.tsx";
import {INCIDENTS_SORT_OPTIONS, sortIncidents, type SortOptions} from "../currentstatus/utils/sortIncidents.ts";
import {DownloadReportButton} from "../DownloadReportButton.tsx";
import {EmptyEventCard} from "../shared/EmptyEventCard.tsx";
import type {PillToolTipProps} from "../StatusPageStatusBar/StatusPageStatusBar.types.ts";
import {StatusPageUptimeBarTooltip} from "../StatusPageUptimeBarTooltip/StatusPageUptimeBarTooltip.tsx";

import styles from "./HistoryOverview.module.scss";

const HISTORY_TABS: NavTabItem[] = [
  {id: HistoryTabType.HISTORY_OVERVIEW, title: "Overview"},
  {id: HistoryTabType.PAST_INCIDENTS, title: "Past Incidents"},
  {id: HistoryTabType.PAST_MAINTENANCE, title: "Past Maintenance"},
  {id: HistoryTabType.COMPONENT_HISTORY, title: "Component History"},
];

const rangeToDates = (range: DateRange): {startDate: string; endDate: string} => {
  return {
    startDate: range.from ? format(range.from, "yyyyMMdd") : format(new Date(), "yyyyMMdd"),
    endDate: range.to ? format(range.to, "yyyyMMdd") : format(new Date(), "yyyyMMdd"),
  };
};

const mapIncident = (i: PublicStatusPageIncident): IncidentModel => {
  return {
    id: i.id,
    statuspage: {} as IncidentModel["statuspage"],
    name: i.name,
    duration: i.duration,
    starts_at: i.starts_at,
    ends_at: i.ends_at,
    incident_type: i.incident_type,
    include_in_global_metrics: true,
    update_component_status: true,
    notify_subscribers: true,
    resolve_components: true,
    send_maintenance_start_notification: false,
    latest_update_incident_state:
      i.latest_update_incident_state ??
      (i.incident_type === IncidentType.SCHEDULED_MAINTENANCE
        ? IncidentState.MAINTENANCE
        : IncidentState.INVESTIGATION),
    latest_update_description: i.latest_update_description ?? "",
    updates: (i.updates ?? []).map((u) => ({
      id: u.id,
      updated_at: u.updated_at,
      description: "",
      description_html: u.description_html,
      is_modified: false,
      incident_state: u.incident_state,
      incident_state_display: u.incident_state_display ?? "",
    })),
    affected_components: (i.affected_components ?? []).map((c) => ({
      id: c.id,
      name: c.name,
      status: c.status,
      status_display: c.status_display,
      description: "",
      component: {id: c.id, name: c.name},
    })),
  };
};

const mapComponentHistoryEntry = (entry: ComponentHistoryEntry): ComponentHistoryTableEntry => {
  return {
    id: entry.id,
    name: entry.name,
    isGroup: entry.is_group,
    majorOutage: entry.major_outage,
    partialOutage: entry.partial_outage,
    degradedPerformance: entry.degraded_performance,
    avgResponseTime: entry.avg_response_time,
    uptimePct: entry.uptime_pct ?? undefined,
    outages: entry.outages,
    downtimeSecs: entry.downtime_secs,
    performanceDataType: entry.performance_data_type,
    serviceUrl: entry.service_url,
    serviceUptimeSla: entry.service_uptime_sla,
    serviceId: entry.service_id,
    isDrillDown: !!entry.service_url,
    children: entry.children?.map(mapComponentHistoryEntry),
  };
};

export interface HistoryOverviewProps {
  activeTab?: HistoryTabType;
  onTabChange?: (tab: HistoryTabType) => void;
  onSwitchToCurrentStatus?: (tab: CurrentStatusTabType) => void;
  // Whether the Current Status Active Incidents / Active Maintenance sub-tabs are reachable —
  // gates the "View Active …" empty-state links so we never link to a hidden tab or sub-tab.
  canViewActiveIncidents?: boolean;
  canViewActiveMaintenance?: boolean;
  mode?: "public" | "private";
  variant?: StatusPageTheme;
  componentLinkTo?: (serviceId: number) => string;
  layout?: StatusPageLayoutPreset;
}

const getUptimeColorClass = (pct: number): string => {
  if (pct >= 99) return styles.uptimeValueGreen;
  if (pct >= 95) return styles.uptimeValueOrange;
  return styles.uptimeValueRed;
};

export const HistoryOverview = ({
  activeTab: controlledActiveTab,
  onTabChange,
  onSwitchToCurrentStatus,
  canViewActiveIncidents = true,
  canViewActiveMaintenance = true,
  mode = "public",
  variant = StatusPageTheme.INSPIRE_PLUS,
  componentLinkTo,
  layout = StatusPageLayoutPreset.EXPANDED,
}: HistoryOverviewProps) => {
  const {slug, id} = useParams<{slug?: string; id?: string}>();
  const [internalActiveTab, setInternalActiveTab] = useState<HistoryTabType>(HistoryTabType.COMPONENT_HISTORY);
  const [incidentSort, setIncidentSort] = useState<SortOptions>("most_recently_created");
  const [maintenanceSort, setMaintenanceSort] = useState<SortOptions>("most_recently_created");
  const [historyOverviewSort, setHistoryOverviewSort] = useState<SortOptions>("most_recently_created");

  const activeTab = controlledActiveTab ?? internalActiveTab;
  const setActiveTab = onTabChange ?? setInternalActiveTab;

  const isInspire = variant === StatusPageTheme.INSPIRE;
  const incidentVariant = isInspire ? "inspire" : "default";

  const isPrivate = mode === "private";
  const parsedId = id ? parseInt(id, 10) : undefined;

  const pageIdentifier = isPrivate ? (id ?? "") : (slug ?? "");
  const {statusPage} = useStatusPageData(pageIdentifier, mode);

  const containerRef = useRef<HTMLDivElement>(null);

  const [theme, setTheme] = useState<string | null>(null);
  const pageTimezone = statusPage?.used_timezone ?? undefined;
  const defaultDays = statusPage?.default_history_date_range ?? 7;
  // Memoized because useDateRange re-resolves whenever this changes identity.
  const withDefault = useMemo(() => {
    const range = defaultDateRangeForDays(defaultDays, pageTimezone);
    return () => range;
  }, [defaultDays, pageTimezone]);

  // The range lives in the URL so it can be shared, and falls back to the page's own setting.
  // legacyDays params keep links interchangeable with the legacy status page; replacing navigation
  // keeps five tried ranges from becoming five history entries.
  const {
    dateRange: selectedRange,
    fixedRangeOption,
    handleRangeChange,
  } = useDateRange({
    fromKey: DATE_RANGE_START_PARAM,
    toKey: DATE_RANGE_END_PARAM,
    fixedRangeKey: "range",
    withDefault,
    timezone: pageTimezone,
    paramFormat: "legacyDays",
    navigateOptions: {replace: true, preventScrollReset: true},
    normalizeSelection: true,
    deriveFixedRange: true,
  });
  const selectedFixedRange = fixedRangeOption?.value;

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const themeRoot = el.closest("[data-theme]");
    if (!themeRoot) return;

    setTheme(themeRoot.getAttribute("data-theme"));
    const observer = new MutationObserver(() => {
      setTheme(themeRoot.getAttribute("data-theme"));
    });
    observer.observe(themeRoot, {attributes: true, attributeFilter: ["data-theme"]});
    return () => observer.disconnect();
  }, []);

  const {startDate, endDate} = rangeToDates(selectedRange);

  // A half-open selection is the picker mid-interaction, not a range to render.
  // A half-open selection is the picker mid-interaction, not a range to render.
  const handleDateRangeChange = (range: DateRange | undefined) => {
    if (range?.from && range.to) {
      handleRangeChange({from: range.from, to: range.to});
    }
  };

  const historySlug = statusPage?.slug ?? "";

  const {data: publicData, loading: publicLoading} = useQuery<{publicStatusPageHistory: PublicStatusPageHistoryResult}>(
    GQL_PUBLIC_STATUSPAGE_HISTORY_QUERY,
    {
      variables: {slug: historySlug, startDate, endDate},
      skip: isPrivate || !historySlug,
      context: {statusPageSlug: historySlug, skipAuthError: true},
      fetchPolicy: "cache-and-network",
    }
  );

  const {data: privateData, loading: privateLoading} = useQuery<{
    privateStatusPageHistory: PublicStatusPageHistoryResult;
  }>(GQL_PRIVATE_STATUSPAGE_HISTORY_QUERY, {
    variables: {id: parsedId ?? 0, startDate, endDate},
    skip: !isPrivate || !parsedId,
  });

  const loading = isPrivate ? privateLoading : publicLoading;
  const historyResult = isPrivate ? privateData?.privateStatusPageHistory : publicData?.publicStatusPageHistory;

  const allIncidents = historyResult?.past_incidents ?? [];
  const componentHistory = (historyResult?.component_history ?? []).map(mapComponentHistoryEntry);
  const pastIncidents = sortIncidents(
    allIncidents.filter((i) => i.incident_type === IncidentType.INCIDENT).map(mapIncident),
    incidentSort
  );
  const pastMaintenance = sortIncidents(
    allIncidents.filter((i) => i.incident_type === IncidentType.SCHEDULED_MAINTENANCE).map(mapIncident),
    maintenanceSort
  );
  const allPastEvents = sortIncidents(allIncidents.map(mapIncident), historyOverviewSort);

  const calculationType = (historyResult?.uptime_calculation_type ??
    statusPage?.uptime_calculation_type ??
    "BY_INCIDENTS") as "BY_INCIDENTS" | "BY_CHECKS";

  const statusMetadata = useMemo<PublicComponentStatusInfo[]>(
    () => statusPage?.component_status_metadata ?? [],
    [statusPage]
  );

  const activeIncidents = useMemo<PublicStatusPageIncident[]>(() => statusPage?.active_incidents ?? [], [statusPage]);

  const upcomingMaintenance = useMemo<PublicStatusPageIncident[]>(
    () => statusPage?.upcoming_maintenance ?? [],
    [statusPage]
  );

  const uptimeBarStart = useMemo(() => selectedRange.from ?? new Date(), [selectedRange.from]);
  const uptimeBarEnd = useMemo(() => selectedRange.to ?? new Date(), [selectedRange.to]);

  // The window deliberately reaches end-of-day: legacy's snake also ends at endOfDay + 1s and fills
  // gaps with SUCCESS (inspire/snake/InspireDateRangeSnake.tsx:38). Do not clamp to `now` — the
  // drilldown does, but only because it mirrors legacy's check-analysis statechart.
  const uptimeBarEvents = useMemo(() => {
    if (!historyResult) return [];
    if (calculationType === "BY_CHECKS") {
      return generateEventsFromDowntimes(historyResult.downtime_history, uptimeBarStart, uptimeBarEnd);
    }
    return generateEventsFromIncidents(historyResult.past_incidents, statusMetadata, uptimeBarStart, uptimeBarEnd);
  }, [historyResult, uptimeBarStart, uptimeBarEnd, calculationType, statusMetadata]);

  const uptimeCalcType =
    calculationType === "BY_CHECKS" ? UptimeCalculationType.BY_CHECKS : UptimeCalculationType.BY_INCIDENTS;

  const allIncidentsForTooltip = useMemo<PublicStatusPageIncident[]>(
    () => [...(historyResult?.past_incidents ?? []), ...activeIncidents, ...upcomingMaintenance],
    [historyResult?.past_incidents, activeIncidents, upcomingMaintenance]
  );

  const serviceComponents = useMemo(
    () => buildServiceComponentMap(statusPage?.components ?? []),
    [statusPage?.components]
  );

  const pillTooltipRenderer = useCallback(
    ({periodStart, periodEnd}: PillToolTipProps): ReactElement => (
      <StatusPageUptimeBarTooltip
        periodStart={periodStart}
        periodEnd={periodEnd}
        incidents={allIncidentsForTooltip}
        statusMetadata={statusMetadata}
        downtimeHistory={historyResult?.downtime_history ?? []}
        uptimeCalculationType={uptimeCalcType}
        serviceComponents={serviceComponents}
        timezone={statusPage?.used_timezone ?? "UTC"}
        timeFormat={statusPage?.time_format}
      />
    ),
    [
      allIncidentsForTooltip,
      historyResult?.downtime_history,
      statusMetadata,
      uptimeCalcType,
      serviceComponents,
      statusPage?.used_timezone,
      statusPage?.time_format,
    ]
  );

  // When enabled, hide the Past Incidents / Past Maintenance sub-tabs that have no events.
  // Mirrors the Current Status tab's hide_empty_tabs_status behavior.
  const hideEmptyTabs = !!statusPage?.hide_empty_tabs_history;

  const visibleTabs = useMemo(() => {
    const baseTabs = HISTORY_TABS.filter((tab) => {
      if (tab.id === HistoryTabType.HISTORY_OVERVIEW)
        return (
          variant === StatusPageTheme.INSPIRE &&
          (!!statusPage?.show_past_incidents || !!statusPage?.show_component_history)
        );
      if (tab.id === HistoryTabType.COMPONENT_HISTORY) return !!statusPage?.show_component_history;
      if (tab.id === HistoryTabType.PAST_INCIDENTS || tab.id === HistoryTabType.PAST_MAINTENANCE)
        return !!statusPage?.show_past_incidents;
      return true;
    });

    let tabs = baseTabs;
    // Only apply hide-empty once the history data has loaded — otherwise sub-tabs would be hidden
    // based on an empty in-flight result and pop in once the query resolves.
    if (hideEmptyTabs && historyResult) {
      tabs = baseTabs.filter((tab) => {
        if (tab.id === HistoryTabType.PAST_INCIDENTS) return pastIncidents.length > 0;
        if (tab.id === HistoryTabType.PAST_MAINTENANCE) return pastMaintenance.length > 0;
        return true;
      });
    }

    // With a single content tab the Overview renders the same content, so drop the redundant
    // Overview tab. This runs after empty sub-tabs are filtered out too, so hide-empty never
    // leaves the Overview duplicating a lone content tab; a zero-content result falls through to
    // the empty state below. (Only the Inspire variant has an Overview tab — a no-op otherwise.)
    const contentTabs = tabs.filter((tab) => tab.id !== HistoryTabType.HISTORY_OVERVIEW);
    if (contentTabs.length <= 1) return contentTabs;
    return tabs;
  }, [
    statusPage?.show_component_history,
    statusPage?.show_past_incidents,
    variant,
    hideEmptyTabs,
    historyResult,
    pastIncidents.length,
    pastMaintenance.length,
  ]);

  useEffect(() => {
    if (visibleTabs.length > 0 && !visibleTabs.some((t) => t.id === activeTab)) {
      setActiveTab(visibleTabs[0].id as HistoryTabType);
    }
  }, [visibleTabs, activeTab, setActiveTab]);

  // Straight from the backend, which runs the same aggregation the legacy status page renders:
  // per-check outages and downtime summed and uptime averaged over the checks eligible for global
  // metrics. Recomputing it here collapsed concurrent outages onto one timeline and counted
  // declared incidents instead of check outages, so the two frontends reported different numbers
  // for the same range.
  const kpiStats = historyResult?.global_metrics ?? null;

  const tabContent: Record<HistoryTabType, ReactNode> = {
    [HistoryTabType.HISTORY_OVERVIEW]: (
      <div className={styles.sectionBlock}>
        {statusPage?.show_past_incidents && (
          <IncidentsList
            incidents={allPastEvents}
            title="Past Events"
            sortBy={historyOverviewSort}
            onSortChange={setHistoryOverviewSort}
            componentStatusRank={{}}
            sortOptions={INCIDENTS_SORT_OPTIONS}
            emptyMessage="There are no Past Events."
            groupByMonth
            timeFormat={statusPage?.time_format as StatusPageTimeFormat | undefined}
            timezone={statusPage?.used_timezone}
            layout={layout}
            variant={incidentVariant}
          />
        )}
        {statusPage?.show_past_incidents && statusPage?.show_component_history && (
          <div className={styles.overviewDivider} />
        )}
        {statusPage?.show_component_history && (
          <ComponentHistoryTable
            data={componentHistory}
            calculationType={calculationType}
            allowDrillDown={statusPage?.allow_drill_down || false}
            componentLinkTo={componentLinkTo}
          />
        )}
      </div>
    ),
    [HistoryTabType.COMPONENT_HISTORY]: (
      <div className={styles.sectionBlock}>
        <ComponentHistoryTable
          data={componentHistory}
          calculationType={calculationType}
          allowDrillDown={statusPage?.allow_drill_down || false}
          componentLinkTo={componentLinkTo}
        />
      </div>
    ),
    [HistoryTabType.PAST_INCIDENTS]: (
      <div className={styles.sectionBlock}>
        <IncidentsList
          incidents={pastIncidents}
          sortBy={incidentSort}
          onSortChange={setIncidentSort}
          componentStatusRank={{}}
          sortOptions={INCIDENTS_SORT_OPTIONS}
          emptyMessage="There are no past Incidents."
          emptyCtaLabel={onSwitchToCurrentStatus && canViewActiveIncidents ? "View Active Incidents" : undefined}
          onEmptyCtaClick={
            onSwitchToCurrentStatus && canViewActiveIncidents
              ? () => onSwitchToCurrentStatus(CurrentStatusTabType.ACTIVE_INCIDENTS)
              : undefined
          }
          groupByMonth
          timeFormat={statusPage?.time_format as StatusPageTimeFormat | undefined}
          timezone={statusPage?.used_timezone}
          layout={layout}
          variant={incidentVariant}
        />
      </div>
    ),
    [HistoryTabType.PAST_MAINTENANCE]: (
      <div className={styles.sectionBlock}>
        <IncidentsList
          incidents={pastMaintenance}
          sortBy={maintenanceSort}
          onSortChange={setMaintenanceSort}
          componentStatusRank={{}}
          sortOptions={INCIDENTS_SORT_OPTIONS}
          emptyMessage="There are no Past Maintenance events."
          emptyCtaLabel={onSwitchToCurrentStatus && canViewActiveMaintenance ? "View Active Maintenance" : undefined}
          onEmptyCtaClick={
            onSwitchToCurrentStatus && canViewActiveMaintenance
              ? () => onSwitchToCurrentStatus(CurrentStatusTabType.ACTIVE_MAINTENANCE)
              : undefined
          }
          groupByMonth
          layout={layout}
          timeFormat={statusPage?.time_format as StatusPageTimeFormat | undefined}
          timezone={statusPage?.used_timezone}
          variant={incidentVariant}
        />
      </div>
    ),
  };

  return (
    <div
      ref={containerRef}
      className={`${styles.historyOverview}${variant === StatusPageTheme.INSPIRE ? " " + styles.historyOverviewInspire : ""}`}
    >
      <div className={classNames(styles.contentBlock, styles.historyHeader)}>
        <h2
          data-testid="history-heading"
          className={classNames(styles.historyTitle, variant === StatusPageTheme.INSPIRE && styles.historyTitleInspire)}
        >
          History
        </h2>
        <div className={styles.headerActions}>
          <DatePicker
            mode="range"
            readOnly
            name="history-date-picker"
            selected={selectedRange}
            fixedRange={selectedFixedRange}
            onChange={handleDateRangeChange}
            timezone={statusPage?.used_timezone ?? "UTC"}
            placement="bottom-end"
            disableFutureDates
            resetDefault={withDefault()}
          />
          {statusPage?.allow_pdf_report && (
            <DownloadReportButton
              mode={mode}
              slug={historySlug}
              pageId={parsedId}
              startDate={startDate}
              endDate={endDate}
              defaultHistoryDateRange={statusPage.default_history_date_range}
            />
          )}
        </div>
      </div>

      {statusPage?.show_summary_metrics && (
        <div className={classNames(styles.contentBlock, styles.kpiRow)}>
          <KPICard
            title="Global Uptime %"
            variant="statuspage"
            valueClassName={kpiStats?.uptime_pct != null ? getUptimeColorClass(kpiStats.uptime_pct * 100) : undefined}
          >
            {/* uptime_pct is the 0-1 fraction legacy reports. */}
            {kpiStats?.uptime_pct != null ? formatUptimePercentage(kpiStats.uptime_pct * 100) : "—"}
          </KPICard>
          <KPICard title="Total Downtime" variant="statuspage">
            {kpiStats?.downtime_secs != null ? formatDowntimeDuration(kpiStats.downtime_secs) : "—"}
          </KPICard>
          <KPICard title="Outage(s)" variant="statuspage">
            {kpiStats?.outages != null ? String(kpiStats.outages) : "—"}
          </KPICard>
        </div>
      )}

      {statusPage?.show_history_snake && (
        <div className={classNames(styles.contentBlock, styles.uptimeBar)}>
          <ScrollableUptimeBar
            start={uptimeBarStart}
            end={uptimeBarEnd}
            timezone={statusPage?.used_timezone ?? "UTC"}
            events={uptimeBarEvents}
            size="large"
            defaultMissingEventStatus={BarEventStatus.OPERATIONAL}
            toolTipContentProps={{className: styles.tooltipContent, ...(theme ? {"data-theme": theme} : {})}}
            pillToolTip={pillTooltipRenderer}
            hoverPill
            stripedNoData
            segmentGapHeight={0}
            enableMobileScroll={isInspire}
          />
        </div>
      )}

      <div className={`${styles.whiteBlock}${variant === StatusPageTheme.INSPIRE ? " " + styles.whiteBlockFlat : ""}`}>
        {visibleTabs.length > 0 ? (
          <>
            <div className={styles.box}>
              <StatusTabs
                tabs={visibleTabs}
                activeTab={activeTab}
                onTabChange={(tab) => setActiveTab(tab as HistoryTabType)}
                className={styles.historyTabs}
              />
            </div>
            <div className={styles.whiteBlockContent}>
              {loading ? <div className={styles.sectionBlock}>Loading…</div> : tabContent[activeTab]}
            </div>
          </>
        ) : (
          // hide_empty_tabs_history removed every event sub-tab for the selected range. The tab
          // itself is still visible because other content keeps it alive (summary metrics, the
          // uptime bar, or a wider default range with events) — show a single empty state here
          // rather than a phantom active tab. A tab with no content at all is hidden upstream.
          <div className={styles.whiteBlockContent}>
            <div className={styles.sectionBlock}>
              <EmptyEventCard message="There are no Past Events." />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
